import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaUserPlus, FaCreditCard, FaDownload, FaLockOpen, FaCheck, FaMobile, FaGlobe } from 'react-icons/fa';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Styled Components
const MembershipSection = styled.section`
  min-height: 100vh;
  background: linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 50%, #2d2d2d 100%);
  padding: 100px 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 70% 30%, rgba(245, 102, 7, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 30% 70%, rgba(245, 102, 7, 0.05) 0%, transparent 50%);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.h2`
  font-size: 3em;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f56607 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.2em;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.3em;
  color: #b0b0b0;
  margin-bottom: 80px;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1.1em;
    margin-bottom: 50px;
  }
`;

const StepsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-bottom: 80px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 30px;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const StepCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 40px 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 1s ease-out;

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }

  &:hover {
    transform: translateY(-10px);
    background: rgba(245, 102, 7, 0.1);
    border-color: rgba(245, 102, 7, 0.3);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #f56607 0%, #ff8c00 100%);
  }
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f56607 0%, #ff8c00 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5em;
  font-weight: 800;
  margin: 0 auto 25px;
  box-shadow: 0 8px 20px rgba(245, 102, 7, 0.3);
`;

const StepIcon = styled.div`
  font-size: 3em;
  color: #f56607;
  margin-bottom: 25px;
  display: flex;
  justify-content: center;
`;

const StepTitle = styled.h3`
  font-size: 1.5em;
  color: #ffffff;
  margin-bottom: 15px;
  font-weight: 700;
`;

const StepDescription = styled.p`
  color: #b0b0b0;
  line-height: 1.6;
  margin-bottom: 20px;
  font-size: 1em;
`;

const StepFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 25px 0 0 0;
  text-align: left;
`;

const StepFeature = styled.li`
  color: #b0b0b0;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95em;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CheckIcon = styled(FaCheck)`
  color: #f56607;
  font-size: 0.9em;
  flex-shrink: 0;
`;

const UnlockSection = styled.div`
  background: linear-gradient(135deg, rgba(245, 102, 7, 0.1) 0%, rgba(255, 140, 0, 0.05) 100%);
  border-radius: 20px;
  padding: 50px 40px;
  border: 1px solid rgba(245, 102, 7, 0.2);
  backdrop-filter: blur(10px);
  animation: ${fadeInUp} 1s ease-out 0.4s both;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(245, 102, 7, 0.1) 0%, transparent 70%);
    animation: ${pulse} 4s ease-in-out infinite;
  }
`;

const UnlockContent = styled.div`
  position: relative;
  z-index: 2;
`;

const UnlockIcon = styled.div`
  font-size: 4em;
  color: #f56607;
  margin-bottom: 25px;
  display: flex;
  justify-content: center;
`;

const UnlockTitle = styled.h3`
  font-size: 2em;
  color: #ffffff;
  margin-bottom: 20px;
  font-weight: 700;
`;

const UnlockDescription = styled.p`
  color: #b0b0b0;
  line-height: 1.6;
  font-size: 1.2em;
  margin-bottom: 30px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const PlatformOptions = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 30px;
  flex-wrap: wrap;
`;

const PlatformOption = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #b0b0b0;
  font-weight: 500;
  font-size: 1.1em;
`;

const PlatformIcon = styled.div`
  color: #f56607;
  font-size: 1.3em;
`;

const CTAButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 15px;
  padding: 20px 50px;
  background: linear-gradient(135deg, #f56607 0%, #ff8c00 100%);
  color: white;
  border: none;
  border-radius: 15px;
  font-size: 1.2em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(245, 102, 7, 0.4);
  margin-top: 30px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(245, 102, 7, 0.6);
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const MembershipSteps: React.FC = () => {
    const handleRegisterClick = () => {
        console.log('Start registration process');
        // Navigate to registration or open modal
    };

    return (
        <MembershipSection>
            <Container>
                <SectionTitle>Ako sa stať členom?</SectionTitle>
                <SectionSubtitle>
                    Jednoduchý 3-krokový proces, ktorý ti umožní získať prístup k exkluzívnym tipom a analýzam
                </SectionSubtitle>

                <StepsContainer>
                    {/* Step 1 */}
                    <StepCard>
                        <StepNumber>1</StepNumber>
                        <StepIcon>
                            <FaUserPlus />
                        </StepIcon>
                        <StepTitle>Zaregistrujte sa</StepTitle>
                        <StepDescription>
                            Vytvorte si účet za pár sekúnd – jednoduchý proces bez zbytočných formalít.
                        </StepDescription>
                        <StepFeatures>
                            <StepFeature>
                                <CheckIcon />
                                Rýchla registrácia
                            </StepFeature>
                            <StepFeature>
                                <CheckIcon />
                                Bezplatný základný účet
                            </StepFeature>
                            <StepFeature>
                                <CheckIcon />
                                Okamžitý prístup k základným funkciám
                            </StepFeature>
                        </StepFeatures>
                    </StepCard>

                    {/* Step 2 */}
                    <StepCard>
                        <StepNumber>2</StepNumber>
                        <StepIcon>
                            <FaCreditCard />
                        </StepIcon>
                        <StepTitle>Zaplaťte členstvo</StepTitle>
                        <StepDescription>
                            Bezpečná platba cez Stripe – viaceré možnosti predplatného na výber.
                        </StepDescription>
                        <StepFeatures>
                            <StepFeature>
                                <CheckIcon />
                                Bezpečná platobná brána Stripe
                            </StepFeature>
                            <StepFeature>
                                <CheckIcon />
                                Možnosť zrušiť predplatné kedykoľvek
                            </StepFeature>
                            <StepFeature>
                                <CheckIcon />
                                Okamžitá aktivácia členstva
                            </StepFeature>
                        </StepFeatures>
                    </StepCard>

                    {/* Step 3 */}
                    <StepCard>
                        <StepNumber>3</StepNumber>
                        <StepIcon>
                            <FaDownload />
                        </StepIcon>
                        <StepTitle>Začnite používať</StepTitle>
                        <StepDescription>
                            Stiahnite si aplikáciu alebo pokračujte na webe – všetok obsah je okamžite dostupný.
                        </StepDescription>
                        <StepFeatures>
                            <StepFeature>
                                <CheckIcon />
                                Mobilná aplikácia pre iOS a Android
                            </StepFeature>
                            <StepFeature>
                                <CheckIcon />
                                Plne funkčný webový prístup
                            </StepFeature>
                            <StepFeature>
                                <CheckIcon />
                                Synchronizácia medzi zariadeniami
                            </StepFeature>
                        </StepFeatures>
                    </StepCard>
                </StepsContainer>

                <UnlockSection>
                    <UnlockContent>
                        <UnlockIcon>
                            <FaLockOpen />
                        </UnlockIcon>
                        <UnlockTitle>Automaticky sa vám odomknú všetky výhody</UnlockTitle>
                        <UnlockDescription>
                            Okamžite po aktivácii členstva získate prístup k exkluzívnym tipom, podcastom, analýzam a celému prémiovému obsahu na stránke aj v aplikácii.
                        </UnlockDescription>

                        <PlatformOptions>
                            <PlatformOption>
                                <PlatformIcon>
                                    <FaMobile />
                                </PlatformIcon>
                                Mobilná aplikácia
                            </PlatformOption>
                            <PlatformOption>
                                <PlatformIcon>
                                    <FaGlobe />
                                </PlatformIcon>
                                Webová platforma
                            </PlatformOption>
                        </PlatformOptions>

                    </UnlockContent>
                </UnlockSection>
            </Container>
        </MembershipSection>
    );
};

export default MembershipSteps;